import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import { useAppState } from "../../hooks/useAppState";
import { useSocketConnected } from "../../hooks/useSocketConnected";
import { appState } from "../../../state";
import { notifyStateChange } from "../../../stateBus";
import { showToast } from "../../../toast";
import { apiFetch } from "../../../api";
import {
  setPartySocketHandlers,
  type PartyAcceptedData,
  type PartyDeclinedData,
  type PartyEndedData,
  type PartyInviteData,
  type PartyQueueChangedData,
  type PartyReactionData,
} from "../../../chatSocket";
import {
  acceptPartyApi,
  addToPartyQueueApi,
  applyPartyAccepted,
  applyPartyDeclined,
  applyPartyEnded,
  applyPartyInvite,
  applyPartyQueueChanged,
  applyPartyReaction,
  declinePartyApi,
  leavePartyApi,
  reactPartyApi,
  removeFromPartyQueueApi,
  removePartyReaction,
  syncPartyApi,
} from "../../../partyApi";
import { getPartyAudioEl, partyTrackUrl, usePartyLiveSync } from "./partySync";
import { PartyInviteDialog } from "./PartyInviteDialog";
import { ReactionBurst } from "../together/ReactionBurst";
import { TogetherChatPanel } from "../together/TogetherChatPanel";
import { TogetherChatStream } from "../together/TogetherChatStream";
import { TogetherChatInput } from "../together/TogetherChatInput";
import { useTogetherChatMessages } from "../together/useTogetherChatMessages";
import type { Track } from "../../hooks/useAudioPlayer";

// Mirrors server.js's ALLOWED_PARTY_REACTIONS exactly - same reasoning as
// TogetherRoot.tsx's REACTION_EMOJIS.
const REACTION_EMOJIS = ["❤️", "🔥", "😍", "😂", "😮", "👏"];
const REACTION_DEBOUNCE_MS = 300;

// Mounted once in AppRoot, always present, self-hiding via appState - same
// convention as TogetherRoot.tsx/GameRoot.tsx. Unlike Together Mode, this
// isn't tied to the Lightbox at all: it drives the singleton
// <audio id="audioPlayer"> element MusicPanel.tsx always keeps mounted
// (see partySync.ts), so shared listening keeps going in the background
// while browsing any screen - the whole point of "self-hosted shared
// listening" is that it isn't limited to a modal the way Watch Together is.
export function PartyRoot() {
  const partySession = useAppState((s) => s.partySession);
  const partyIncomingInvite = useAppState((s) => s.partyIncomingInvite);
  const partyReactions = useAppState((s) => s.partyReactions);
  const partyInviteDialogOpen = useAppState((s) => s.partyInviteDialogOpen);
  const presenceUsers = useAppState((s) => s.presenceUsers);
  const socketConnected = useSocketConnected();

  // Real presence data, same lookup TogetherRoot.tsx uses for its own
  // status pill - needed for TogetherChatStream.tsx/TogetherChatPanel.tsx's
  // shared avatar treatment now that both render a real per-message avatar
  // rather than just a text sender name.
  const partnerAvatarFilename = partySession ? (presenceUsers.find((u) => u.id === partySession.peerUserId)?.avatarFilename ?? null) : null;

  const [leaving, setLeaving] = useState(false);
  const [respondingToInvite, setRespondingToInvite] = useState(false);
  const [reactionTrayDisabled, setReactionTrayDisabled] = useState(false);
  const reactionGuardRef = useRef(false);
  const [chatOpen, setChatOpen] = useState(false);
  const [panelOpen, setPanelOpen] = useState(false);
  const [libraryTracks, setLibraryTracks] = useState<Track[]>([]);
  const [addMenuAnchor, setAddMenuAnchor] = useState<HTMLElement | null>(null);

  const { applyRemoteSync, loadedFilenameRef } = usePartyLiveSync(partySession);

  const activeConversationId = partySession?.status === "active" ? partySession.conversationId : null;
  const { messages: chatMessages, streamQueue, appendSentMessage, removeFromStream } = useTogetherChatMessages(activeConversationId);

  useEffect(() => {
    setPartySocketHandlers({
      onPartyInvite: (data: PartyInviteData) => applyPartyInvite(data),
      onPartyAccepted: (data: PartyAcceptedData) => applyPartyAccepted(data),
      onPartyDeclined: (data: PartyDeclinedData) => applyPartyDeclined(data),
      onPartyQueueChanged: (data: PartyQueueChangedData) => applyPartyQueueChanged(data),
      onPartySync: (data) => applyRemoteSync(data),
      onPartyReaction: (data: PartyReactionData) => applyPartyReaction(data),
      onPartyEnded: (data: PartyEndedData) => applyPartyEnded(data),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!partySession) {
      setChatOpen(false);
      setPanelOpen(false);
    }
  }, [partySession]);

  useEffect(() => {
    if (!partyInviteDialogOpen && !panelOpen) return;
    (async () => {
      try {
        const res = await apiFetch("/api/music");
        if (!res.ok) return;
        const data = await res.json();
        setLibraryTracks(Array.isArray(data.tracks) ? data.tracks : []);
      } catch {
        setLibraryTracks([]);
      }
    })();
  }, [partyInviteDialogOpen, panelOpen]);

  async function handleAccept(): Promise<void> {
    if (!partyIncomingInvite) return;
    setRespondingToInvite(true);
    const result = await acceptPartyApi(partyIncomingInvite.sessionId);
    setRespondingToInvite(false);
    if (!result.ok) showToast(result.error || "Failed to accept invite.", "error");
  }

  async function handleDecline(): Promise<void> {
    if (!partyIncomingInvite) return;
    setRespondingToInvite(true);
    await declinePartyApi(partyIncomingInvite.sessionId);
    setRespondingToInvite(false);
  }

  async function handleLeave(): Promise<void> {
    if (!partySession) return;
    setLeaving(true);
    getPartyAudioEl()?.pause();
    loadedFilenameRef.current = null;
    await leavePartyApi(partySession.id);
    setLeaving(false);
  }

  function handleReact(emoji: string): void {
    if (!partySession || partySession.status !== "active") return;
    if (reactionGuardRef.current) return;
    reactionGuardRef.current = true;
    setReactionTrayDisabled(true);
    setTimeout(() => {
      reactionGuardRef.current = false;
      setReactionTrayDisabled(false);
    }, REACTION_DEBOUNCE_MS);
    reactPartyApi(partySession.id, emoji);
  }

  // Discrete track jumps (pick from queue, next/prev) send their own
  // explicit sync rather than waiting on the audio element's "play" event
  // to fire the DOM-listener-driven sync in partySync.ts - same reasoning
  // as togetherSync.ts's photo-navigation effect vs. its video listeners.
  function handleSelectTrack(index: number): void {
    if (!partySession || partySession.status !== "active") return;
    const track = partySession.queue[index];
    const audio = getPartyAudioEl();
    if (!track || !audio) return;

    loadedFilenameRef.current = track.filename;
    audio.src = partyTrackUrl(track.filename);
    audio.addEventListener(
      "loadedmetadata",
      () => {
        audio.play()?.catch(() => {});
      },
      { once: true },
    );

    appState.partySession = { ...partySession, currentIndex: index, position: 0, playing: true };
    notifyStateChange();
    syncPartyApi(partySession.id, { currentIndex: index, position: 0, playing: true });
  }

  function handlePlayPause(): void {
    if (!partySession || partySession.status !== "active" || partySession.currentIndex < 0) return;
    const audio = getPartyAudioEl();
    const track = partySession.queue[partySession.currentIndex];
    if (!audio || !track) return;

    if (loadedFilenameRef.current !== track.filename) {
      handleSelectTrack(partySession.currentIndex);
      return;
    }
    if (audio.paused) audio.play()?.catch(() => {});
    else audio.pause();
  }

  function handleNext(): void {
    if (!partySession) return;
    if (partySession.currentIndex + 1 < partySession.queue.length) handleSelectTrack(partySession.currentIndex + 1);
  }

  function handlePrev(): void {
    if (!partySession) return;
    if (partySession.currentIndex - 1 >= 0) handleSelectTrack(partySession.currentIndex - 1);
  }

  async function handleAddTrack(filename: string): Promise<void> {
    setAddMenuAnchor(null);
    if (!partySession) return;
    const result = await addToPartyQueueApi(partySession.id, filename);
    if (!result.ok) showToast(result.error || "Failed to add track.", "error");
  }

  async function handleRemoveTrack(queueId: string): Promise<void> {
    if (!partySession) return;
    const wasCurrent = partySession.currentIndex >= 0 ? partySession.queue[partySession.currentIndex]?.queueId : null;
    const result = await removeFromPartyQueueApi(partySession.id, queueId);
    if (result.ok && wasCurrent === queueId) {
      getPartyAudioEl()?.pause();
      loadedFilenameRef.current = null;
    }
  }

  const currentTrack = partySession && partySession.currentIndex >= 0 ? partySession.queue[partySession.currentIndex] : null;

  // Portaled to document.body, outside #react-root - same reasoning as
  // TogetherRoot.tsx/GameRoot.tsx (MUI's ModalManager marks every other
  // child of #react-root aria-hidden while any Dialog is open).
  return createPortal(
    <>
      <PartyInviteDialog
        open={partyInviteDialogOpen}
        onClose={() => {
          appState.partyInviteDialogOpen = false;
          notifyStateChange();
        }}
      />

      {partyIncomingInvite && (
        <div
          className="glass p-3"
          style={{
            position: "fixed",
            bottom: "calc(16px + env(safe-area-inset-bottom, 0px))",
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: 1400,
            minWidth: 280,
            maxWidth: "90vw",
          }}
        >
          <div className="d-flex align-items-center gap-2 mb-2">
            <i className="bi bi-music-note-beamed text-pink" aria-hidden="true" />
            <span className="fw-semibold">{partyIncomingInvite.fromName} wants to listen together</span>
          </div>
          <div className="d-flex gap-2 justify-content-end">
            <Button size="small" onClick={handleDecline} disabled={respondingToInvite}>
              Decline
            </Button>
            <Button size="small" variant="contained" color="primary" onClick={handleAccept} disabled={respondingToInvite}>
              Join
            </Button>
          </div>
        </div>
      )}

      {partySession && (
        <div
          className="glass px-3 py-2 d-flex align-items-center gap-2"
          style={{
            position: "fixed",
            top: "calc(16px + env(safe-area-inset-top, 0px))",
            left: 16,
            maxWidth: "calc(100vw - 32px)",
            zIndex: 1400,
          }}
        >
          <span
            title={socketConnected ? "Connected" : "Reconnecting…"}
            aria-label={socketConnected ? "Connected" : "Reconnecting"}
            style={{
              width: 8,
              height: 8,
              borderRadius: "50%",
              flexShrink: 0,
              background: socketConnected ? "#2ecc71" : "#f0b93d",
            }}
          />
          <i className="bi bi-music-note-beamed text-pink flex-shrink-0" aria-hidden="true" />
          <span className="small fw-semibold text-truncate">
            {partySession.status === "pending"
              ? `Waiting for ${partySession.peerName}…`
              : currentTrack
                ? currentTrack.title
                : `Listening with ${partySession.peerName}`}
          </span>
          {partySession.status === "active" && (
            <>
              <IconButton aria-label="Toggle party panel" size="small" onClick={() => setPanelOpen((v) => !v)} sx={{ flexShrink: 0 }}>
                <i className={`bi ${panelOpen ? "bi-chevron-up" : "bi-chevron-down"}`} aria-hidden="true" />
              </IconButton>
              <IconButton aria-label="Toggle chat" size="small" onClick={() => setChatOpen((v) => !v)} sx={{ flexShrink: 0 }}>
                <i className="bi bi-chat-dots-fill" aria-hidden="true" />
              </IconButton>
            </>
          )}
          <IconButton aria-label="Leave party" size="small" onClick={handleLeave} disabled={leaving} sx={{ flexShrink: 0 }}>
            <i className="bi bi-x-lg" aria-hidden="true" />
          </IconButton>
        </div>
      )}

      {partySession?.status === "active" && panelOpen && (
        <div
          className="glass p-2"
          style={{
            position: "fixed",
            top: "calc(64px + env(safe-area-inset-top, 0px))",
            left: 16,
            width: 300,
            maxWidth: "calc(100vw - 32px)",
            maxHeight: "60vh",
            overflowY: "auto",
            zIndex: 1400,
            display: "flex",
            flexDirection: "column",
            gap: 8,
          }}
        >
          <div className="d-flex align-items-center justify-content-center gap-2 py-1">
            <IconButton aria-label="Previous track" size="small" onClick={handlePrev} disabled={partySession.currentIndex <= 0}>
              <i className="bi bi-skip-start-fill" aria-hidden="true" />
            </IconButton>
            <IconButton aria-label="Play or pause" onClick={handlePlayPause} disabled={partySession.currentIndex < 0}>
              <i className="bi bi-play-pause-fill" aria-hidden="true" style={{ fontSize: "1.3rem" }} />
            </IconButton>
            <IconButton
              aria-label="Next track"
              size="small"
              onClick={handleNext}
              disabled={partySession.currentIndex < 0 || partySession.currentIndex + 1 >= partySession.queue.length}
            >
              <i className="bi bi-skip-end-fill" aria-hidden="true" />
            </IconButton>
          </div>

          <div className="d-flex align-items-center justify-content-between">
            <span className="small fw-semibold">Queue</span>
            <IconButton
              aria-label="Add track to queue"
              size="small"
              onClick={(e) => setAddMenuAnchor(e.currentTarget)}
              disabled={!libraryTracks.length}
            >
              <i className="bi bi-plus-lg" aria-hidden="true" />
            </IconButton>
            <Menu anchorEl={addMenuAnchor} open={!!addMenuAnchor} onClose={() => setAddMenuAnchor(null)}>
              {libraryTracks.map((t) => (
                <MenuItem key={t.filename} onClick={() => handleAddTrack(t.filename)}>
                  {t.title}
                </MenuItem>
              ))}
            </Menu>
          </div>

          {!partySession.queue.length && <div className="small muted">Nothing queued yet - add a track above.</div>}
          <ul className="list-group" role="list" aria-label="Party queue">
            {partySession.queue.map((item, idx) => (
              <li
                key={item.queueId}
                className={`list-group-item d-flex align-items-center justify-content-between gap-2 ${idx === partySession.currentIndex ? "is-playing" : ""}`}
              >
                <button
                  type="button"
                  className="btn btn-sm text-start flex-grow-1 min-w-0 text-truncate"
                  onClick={() => handleSelectTrack(idx)}
                  style={{ background: "none", border: "none" }}
                >
                  {idx === partySession.currentIndex && partySession.playing ? (
                    <i className="bi bi-volume-up-fill me-1 text-pink" aria-hidden="true" />
                  ) : null}
                  {item.title}
                </button>
                <IconButton aria-label={`Remove ${item.title}`} size="small" onClick={() => handleRemoveTrack(item.queueId)}>
                  <i className="bi bi-x" aria-hidden="true" />
                </IconButton>
              </li>
            ))}
          </ul>

          <div className="d-flex align-items-center gap-1 pt-1" role="group" aria-label="Send a reaction">
            {REACTION_EMOJIS.map((emoji) => (
              <IconButton
                key={emoji}
                size="small"
                aria-label={`React with ${emoji}`}
                onClick={() => handleReact(emoji)}
                disabled={reactionTrayDisabled}
                sx={{ fontSize: "1.1rem", p: 0.5 }}
              >
                {emoji}
              </IconButton>
            ))}
          </div>
        </div>
      )}

      {activeConversationId && chatOpen && (
        <div
          style={{
            position: "fixed",
            left: 16,
            right: 16,
            bottom: "calc(16px + env(safe-area-inset-bottom, 0px))",
            zIndex: 1400,
            borderRadius: "var(--radius)",
            overflow: "hidden",
            background: "var(--surface-opaque)",
            boxShadow: "var(--shadow)",
          }}
        >
          <TogetherChatInput
            conversationId={activeConversationId}
            onSent={appendSentMessage}
            embedded
            partnerName={partySession?.peerName}
          />
        </div>
      )}

      {activeConversationId && (
        <TogetherChatStream queue={streamQueue} partnerAvatarFilename={partnerAvatarFilename} onExpire={removeFromStream} />
      )}

      <TogetherChatPanel
        open={chatOpen && !!activeConversationId}
        messages={chatMessages}
        partnerAvatarFilename={partnerAvatarFilename}
        onClose={() => setChatOpen(false)}
      />

      {partyReactions.map((r) => (
        <ReactionBurst key={r.id} emoji={r.emoji} onDone={() => removePartyReaction(r.id)} />
      ))}
    </>,
    document.body,
  );
}
